/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import DotsCanvasRegl from 'components/dotsCanvasRegl'
import { Scrollama, Step } from 'react-scrollama'
import { Link } from '@ta-interaktiv/contentbricks'
import { Autocomplete } from '@material-ui/lab'
import { TextField } from '@material-ui/core'
import { InteractiveMap } from 'components/interactiveMap'
import { MiniScrollList } from 'components/miniScrollList'
import businessesList from './businessesTypeList.js'
import * as d3 from 'd3'

const communesGeo = require('../interactiveMap/municipalities_vd.json')
const chartsData = require('./standalone-data.csv')
const ARTICLE_ID = '499488733621'

export function AppComponent() {
  const [showMap, setShowMap] = useState(false)
  const [stage, setStage] = useState(0)
  const [selectedMuniciaplity, setSelectedMunicipality] = useState(0)
  const [selectedActivity, setSelectedActivity] = useState(0)
  // Data used in the list
  let dataList = Object.entries(
    chartsData.find(o => +o.ofsnr === selectedMuniciaplity)
  )
    .filter(o => !['ofsnr', 'name'].some(el => o[0].includes(el)))
    .reduce((a, b) => [...a, [+b[0], b[1].split(',').map(Number)]], [])
    .sort((a, b) => b[1][0] - a[1][0])

  // Put 'All' in first position
  const a = dataList.find(o => o[0] === 0)
  dataList = dataList.filter(o => o[0] !== 0)
  dataList.unshift(a)

  console.log(chartsData)
  console.log(dataList)

  // Data used in the map
  const dataMap = chartsData.reduce(
    (a, b) => [
      ...a,
      {
        ofsnr: +b.ofsnr,
        values: b[selectedActivity].split(',').map(Number)
      }
    ],
    []
  )

  console.log(dataMap)

  // Data used in the businesses types lists
  console.log(chartsData)
  let businessesTypeList = Object.entries(chartsData[0])
    .filter(o => !['ofsnr', 'name'].some(el => o[0].includes(el)))
    .reduce((a, b) => [...a, { name: businessesList[+b[0]], id: +b[0] }], [])
    .sort((a, b) => a.name.localeCompare(b.name))

  // Put 'All' in first position
  const b = businessesTypeList.find(o => o.id === 0)
  businessesTypeList = businessesTypeList.filter(o => o.id !== 0)
  businessesTypeList.unshift(b)

  console.log(businessesTypeList)

  const tableColor = d3
    .scaleLinear()
    .domain([
      Math.max(...dataList.reduce((a, b) => [...a, b[1][0]], [])),
      Math.min(...dataList.reduce((a, b) => [...a, b[1][0]], []))
    ])
    .interpolate(d3.interpolateRgb)
    .range([d3.rgb('#ea75bedd'), d3.rgb('#ea75be00')])

  console.log(businessesTypeList)

  return (
    <div className='index'>
      <Masthead inverted articleId={ARTICLE_ID} enablePaywall={false} />
      <ThemeSwitch />

      <div className='scrollytelling-container'>
        <div style={{ position: 'sticky', top: '0px' }}>
          <DotsCanvasRegl stage={stage} />
        </div>
        <header className='ui vertical  very fitted  segment'>
          <div className='ui container'>
            <div className='ui header inverted'>
              <h1>Vie et mort des petites entreprises vaudoises</h1>
              <h2>
                Coiffeur de quartier, magasin de prêt-à-porter familial ou
                encore école de danse communale… Des milliers d'entreprises
                indépendantes sont actuellement menacées par la pandémie de
                Covid-19. Mais que savons-nous réellement de ces affaires qui
                forment le cœur de notre vie sociale et économique? Plongée
                graphique inédite dans la vie de{' '}
                <span className='colored'>
                  dizaines de milliers d'entreprises
                </span>{' '}
                vaudoises.
              </h2>
              <div className='ta metainfo'>
                <div className='ta author'>
                  Mathieu Rudaz, Cécile Denayrouse
                </div>
                <div className='ta updated'>Mis à jour le 10.12.2020</div>
              </div>
            </div>
          </div>
        </header>

        <div className='scrollama-container'>
          <div className='steps'>
            <Scrollama
              debug={false}
              offset={0.5}
              onStepEnter={args => {
                setStage(args.data)
                console.log('Stage ' + args.data + ' - ' + args.direction)
              }}
              onStepExit={args => {
                console.log('EXIT ' + args.data)
              }}
            >
              <Step data={0}>
                <div style={{ height: '25vh' }} />
              </Step>
              <Step data={1}>
                <div style={{ height: '25vh' }} />
              </Step>
              <Step data={1}>
                <div style={{ height: '80vh' }}>
                  <div className='ui container text message'>
                    <p className='special'>
                      <b>État des lieux</b>
                    </p>
                    <p>
                      Depuis 1883, quelque 150'000 entreprises vaudoises ont été
                      enregistrées au Registre du commerce. Pour mieux
                      visualiser l'ampleur de ces données séculaires, nous avons
                      symbolisé chacune d'elles par un{' '}
                      <i className='ui icon circle' /> point.
                    </p>
                    <p>
                      Café, atelier d'architecture, boucherie, banque ou salon
                      de coiffure… <b className='colored'>Toute entreprise</b>{' '}
                      au chiffre d'affaires supérieur à 100'000 francs ayant un
                      jour existé dans le canton <b>est représentée ici</b>.
                    </p>
                  </div>
                </div>
              </Step>
              <Step data={2}>
                <div style={{ height: '50vh' }} />
              </Step>
              <Step data={2}>
                <div style={{ height: '80vh' }}>
                  <div className='ui container text message'>
                    <p className='special'>
                      <b>Le choix des petites entreprises</b>
                    </p>
                    <p>
                      Parmi ces 150'000 entreprises,{' '}
                      <b className='colored'>un tiers</b> environ sont des
                      entreprises individuelles, la forme juridique la plus
                      courante en Suisse.
                    </p>
                    <p>
                      Celles-ci regroupent les{' '}
                      <b>activités étroitement liées au propriétaire</b>. Les
                      petits commerces, les artisans, les médecins, les avocats
                      et les entreprises commerciales locales en font partie.
                    </p>
                  </div>
                </div>
              </Step>
              <Step data={3}>
                <div style={{ height: '50vh' }} />
              </Step>
              <Step data={3}>
                <div style={{ height: '50vh' }}>
                  <div className='ui container text message'>
                    <p className='special'>
                      <b>Chronologie</b>
                    </p>
                    <p>
                      Pour la seule année 2018, le canton de Vaud a enregistré{' '}
                      <b className='colored'>
                        1587 nouvelles petites entreprises
                      </b>
                      . Une véritable frénésie entrepreneuriale puisque le
                      Registre du commerce en recense presque autant pour toute
                      la période des Trente Glorieuses. Le canton comptait alors
                      deux fois moins d'habitants qu'aujourd'hui.
                    </p>
                    <p>
                      Année après année, le nombre de création d'entreprises
                      individuelles <b>ne cesse d'augmenter</b> au gré des modes
                      et de la démographie.
                    </p>
                  </div>
                </div>
              </Step>
              <Step data={3001}>
                <div style={{ height: '100vh' }} />
              </Step>
              <Step data={3001}>
                <div style={{ height: '50vh' }}>
                  <div className='ui container text message'>
                    <p className='special'>
                      <b>Des entreprises et des modes</b>
                    </p>
                    <p>
                      À titre d'exemple, les{' '}
                      <i className='ui icon circle color2' /> points blancs que
                      vous venez de voir représentent les entreprises de{' '}
                      <b style={{ color: '#ffffff' }}>location de vidéo</b> qui
                      ont connu leur heure de gloire dans les années 90. La
                      dernière inscription de ce type d'entreprise dans le
                      registre remonte à l'année 2009.
                    </p>
                    <p>
                      Tendance inverse, l'augmentation du nombre de{' '}
                      <i className='ui icon circle color1' /> points roses
                      illustre l'irrésistible ascension{' '}
                      <b className='colored'>des barbiers</b>, souvent symboles
                      de gentrification, qui ont commencé à fleurir dans le
                      canton ces dernières années.
                    </p>
                  </div>
                </div>
              </Step>
              <Step data={3001}>
                <div style={{ height: '25vh' }} />
              </Step>
              <Step data={3002}>
                <div style={{ height: '25vh' }} />
              </Step>
              <Step data={5}>
                <div style={{ height: '50vh' }} />
              </Step>
              <Step data={5}>
                <div style={{ height: '80vh' }}>
                  <div className='ui container text message'>
                    <p className='special'>
                      <b>Des groupes distincts</b>
                    </p>
                    <p>
                      Nous avons classé toutes ces entreprises à l'aide d'un
                      algorithme d'apprentissage automatique. Celui-ci nous a
                      permis de créer près de{' '}
                      <b className='colored'>50 groupes</b> distincts qui
                      associent des domaines d'activité similaires.
                    </p>
                    {/* <p>
                        Les entreprises dont l'activité est inconnue ont n'ont
                        pas été prises en compte, ce qui ramène leur nombre à{' '}
                        <b>42'000</b>.
                      </p> */}
                    <MiniScrollList
                      list={businessesTypeList.reduce(
                        (a, b) => [...a, b.name],
                        []
                      )}
                    />
                  </div>
                </div>
              </Step>
              <Step data={6}>
                <div style={{ height: '50vh' }} />
              </Step>
              <Step data={6}>
                <div style={{ height: '50vh' }}>
                  <div className='ui container text message'>
                    <p className='special'>
                      <b>Une durée de vie très courte</b>
                    </p>
                    <p>
                      Nous avons calculé la <b>durée de vie moyenne</b> de
                      chacun de ces groupes.
                    </p>
                    <p>
                      Mais tout n'est pas simple dans le monde des petites
                      entreprises: sur l'ensemble des 42'000 petites entreprises
                      vaudoises créées depuis 1883 et dont nous connaissons
                      l'activité, seules{' '}
                      <b className='colored'>74% passent le cap des 3 ans</b>.
                    </p>
                  </div>
                </div>
              </Step>
              <Step data={7}>
                <div style={{ height: '50vh' }} />
              </Step>
              <Step data={7}>
                <div style={{ height: '50vh' }}>
                  <div className='ui container text message'>
                    <p className='special'>
                      <b>Bilan après 6 ans</b>
                    </p>
                    <p>
                      L'analyse des données nous apprend également que le
                      passage des 6 ans est moins critique:{' '}
                      <b>58% des affaires</b> continuent leur activité une fois
                      cet anniversaire passé.
                    </p>

                    <p>
                      Le cap des 2 à 5 ans est considéré comme le plus délicat,
                      une étape cruciale qui demande de surmonter de nombreuses
                      difficultés.
                    </p>
                  </div>
                </div>
              </Step>
              <Step data={8}>
                <div style={{ height: '50vh' }} />
              </Step>
              <Step data={8}>
                <div style={{ height: '50vh' }}>
                  <div className='ui container text message'>
                    <p className='special'>
                      <b>Bilan après 20 ans</b>
                    </p>
                    <p>
                      Seules <b>23% des entreprises</b> survivent après 20 ans.
                    </p>
                    <p>
                      Bien sûr, une entité peut-être radiée du Registre du
                      commerce pour d'autres raisons que la faillite. Parmi
                      celles-ci, on trouve notamment le décès du titulaire, la
                      remise de l'exploitation ou un chiffre d'affaires
                      inférieur à 100'000 francs.
                    </p>
                  </div>
                </div>
              </Step>
              <Step data={9}>
                <div style={{ height: '50vh' }} />
              </Step>
              <Step data={9}>
                <div style={{ height: '50vh' }}>
                  <div className='ui container text message'>
                    <p className='special'>
                      <b>Et après...</b>
                    </p>
                    <p>
                      Il ne reste qu'
                      <b className='colored'>une centaine d'affaires</b> dans le
                      cercle très fermé des <b>entreprises quinquagénaires</b>,
                      créées avant 1970 et toujours en activité aujourd'hui.
                      Certaines d'entre elles ont su imposer leur empreinte dans
                      le canton et font désormais partie du patrimoine vaudois.
                    </p>
                  </div>
                </div>
              </Step>
              <Step data={10}>
                <div style={{ height: '50vh' }} />
              </Step>
            </Scrollama>
          </div>
        </div>
      </div>

      <div className='ui vertical segment inverted'>
        <div className='ui container text message'>
          <p className='special'>
            <b>Secteurs d'activité</b>
          </p>
          <p>
            Bien sûr, ces tendances sont{' '}
            <b>différentes d'une époque à l'autre</b> et d'un{' '}
            <b>secteur d'activité à l'autre</b>. L'origine du fondateur et la
            commune qui accueille la petite entreprise entrent aussi dans
            l'équation.
          </p>
          <p>
            Voici par exemple, dans les deux graphiques qui suivent, la courbe
            des cafés-restaurants et des salons de coiffure créés il y a{' '}
            <b className='colored'>une génération</b>, soit 25 ans.
          </p>
        </div>
        <div className='ui vertical segment'>
          <div className='ui container'>
            <div className='ui small-multiple-container stackable two column grid'>
              <div className='column'>
                <img src={require('../charts/chart-coiffeur-lifespan.svg')} />
              </div>
              <div className='column'>
                <img src={require('../charts/chart-restaurant-lifespan.svg')} />
              </div>
            </div>
          </div>
        </div>
        <div className='ui text container message'>
          <p>
            Ces courbes représentent le nombre d'affaires créées en 1994 et ce
            qu'il en reste, année après année. Dans ces deux cas particuliers,
            on remarque que les cafés-restaurants, qui comptent davantage
            d'inscriptions que les salons de coiffure pour cette année-là,
            disparaissent des radars plus rapidement que la moyenne cantonale.
          </p>
        </div>
        <div className='ui container text message'>
          <p className='special'>
            <b>Tous les chiffres</b>
          </p>
          <p>
            La durée de vie médiane d'une petite entreprise dans le canton de
            Vaud est de <b className='colored'>7 ans et demi</b>. Alors, en
            moyenne cantonale et communale, quelle est la durée de vie de{' '}
            <b>chaque catégorie d'activité</b>? Vous trouverez la réponse dans
            le tableau et la carte qui suivent.
          </p>
          <p>
            <small>
              Seules les entreprises radiées du Registre du commerce ont été
              prises en compte.
            </small>
          </p>
        </div>
      </div>

      {!showMap && (
        <div className='ui vertical segment inverted'>
          <div
            className='ui text centered message standard container'
            style={{ textAlign: 'center' }}
          >
            <b
              className='inline-button'
              onClick={() => {
                setShowMap(!showMap)
              }}
            >
              <i className='ui icon map' />
              Afficher la carte
            </b>
          </div>
          <div className='ui text container message standard'>
            <Autocomplete
              className='autocompleteDropdown'
              value={chartsData.find(d => +d.ofsnr === selectedMuniciaplity)}
              options={chartsData.reduce(
                (a, b) => [...a, { id: +b.ofsnr, name: b.name }],
                []
              )}
              getOptionLabel={option => option.name}
              onChange={(e, v) => {
                console.log(v.id)
                if (v) setSelectedMunicipality(v.id)
              }}
              renderInput={params => (
                <TextField {...params} variant='outlined' />
              )}
              autoSelect
              blurOnSelect
              disableClearable
              noOptionsText="Nous n'avons pas de chiffres pour cette commune"
            />
          </div>
          <div className='ui container'>
            <table className='ui selectable inverted unstackable table'>
              <thead>
                <tr>
                  <th>Activité</th>
                  <th>Durée de vie médiane en années</th>
                  <th className='right aligned'>Nombre</th>
                  <th className='right aligned'>Pourcentage</th>
                </tr>
              </thead>
              <tbody>
                {dataList.map((p, i) =>
                  p[1][0] ? (
                    <tr key={`row-${i}`}>
                      <td>{businessesList[p[0]]}</td>
                      <td style={{ backgroundColor: tableColor(p[1][0]) }}>
                        {p[1][0]}
                      </td>
                      <td className='right aligned'>{p[1][1]}</td>
                      <td className='right aligned'>{p[1][2]}%</td>
                    </tr>
                  ) : (
                    ''
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showMap && (
        <div className='ui vertical segment inverted'>
          <div
            className='ui text centered message standard container'
            style={{ textAlign: 'center' }}
          >
            <b
              className='inline-button'
              onClick={() => {
                setShowMap(!showMap)
              }}
            >
              <i className='ui icon file alternate' />
              Afficher la liste
            </b>
          </div>
          <div className='ui text container message standard'>
            <Autocomplete
              className='autocompleteDropdown'
              value={businessesTypeList.find(d => d.id === selectedActivity)}
              options={businessesTypeList}
              getOptionLabel={option => option.name}
              onChange={(e, v) => {
                if (v) setSelectedActivity(v.id)
              }}
              renderInput={params => (
                <TextField {...params} variant='outlined' />
              )}
              autoSelect
              blurOnSelect
              disableClearable
              noOptionsText="Nous n'avons pas de chiffres pour ce type d'activité"
            />
          </div>
          <div className='ui container'>
            <InteractiveMap filteredCommunes={communesGeo} data={dataMap} />
          </div>
        </div>
      )}

      <div className='ui vertical segment inverted' id='methodology'>
        <div className='ui text container'>
          <h4 className='ui header'>Méthode</h4>
          <p>
            Les données proviennent d'une compilation, datant du mois de
            novembre 2019, issue du{' '}
            <Link href='https://www.rc2.vd.ch/registres/hrcintapp-pub/companySearch.action'>
              moteur de recherche
            </Link>{' '}
            du Registre du commerce vaudois.
          </p>
          <p>
            Les groupes d'activité ont été définis sur la base de la description
            donnée par le créateur de l'entreprise. Celle-ci n'est régie par
            aucune nomenclature. Pour regrouper les entreprises du même secteur,
            nous avons donc utilisé une méthode de partitionnement des données
            en{' '}
            <Link href='https://fr.wikipedia.org/wiki/K-moyennes'>
              <i>k</i>-moyennes.
            </Link>
            <br />
            Nous avons ensuite affiné le résultat en sélectionnant des mot-clés
            présents ou non dans la description laissée à la liberté du
            fondateur. Ainsi, une entreprise décrite simplement comme
            «horlogerie et bijouterie» partage le même groupe qu'une affaire
            «d'achat et vente de montres et bijoux anciens».
            <br /> Cette méthode a des inconvénients puisque certaines
            entreprises regroupent plusieurs activités différentes, comme la
            vente de vêtements <i>et</i> de bijoux. Dans ce cas précis, nous
            pouvons exclure le mot <i>vêtement</i> du groupe bijouterie mais,
            pour un petit pourcentage d'affaires, les frontières entre chaque
            groupe sont parfois un peu floues. <br /> Les entreprises dont
            l'activité est inconnue n'ont pas été prises en compte
          </p>
          <p>
            Nous n'avons conservé que les entreprises qui entrent dans la
            catégorie <b>entreprise individuelle</b>. Cette forme juridique est,
            elle, bien définie dans le Registre du commerce. Cela exclut du
            résultat les succursales d'une entreprise suisse, les sociétés à
            responsabilité limitée, les sociétés anonymes, fondations, sociétés
            en nom collectif et les coopératives.
          </p>
        </div>
      </div>

      <Footer
        inverted
        title='Comment jugez-vous ce contenu?'
        questions={[
          'Était-il informatif?',
          'La mise en forme vous plaît-elle?',
          'Voudriez-vous voir davantage de ce type de contenu?'
        ]}
        hidePortfolioTeaser
        mailTo='mathieu.rudaz@tamedia.ch'
        locale='fr'
        articleId={ARTICLE_ID}
        credits={[
          ['Recherches et réalisation', 'Mathieu Rudaz'],
          ['Textes', 'Mathieu Rudaz et Cécile Denayrouse']
        ]}
      />
    </div>
  )
}
